/**
 * PCC locale x service content builder.
 * Renders the authored, fixed-order copy in ./locale-service-safe.ts for every
 * locale x service pair. The old seeded/shuffled pools (unverifiable volume,
 * acceptance and SLA claims) were removed; only UI labels remain here.
 */
import type { PccService } from "@/data/pcc";
import type { PccLocale } from "@/data/pcc/locale-copy";
import { SAFE_PCC_COPY } from "./locale-service-safe";

export interface LocaleSvcSection { heading: string; html: string }
export interface LocaleSvcContent {
  h1: string;
  lead: string;
  sections: LocaleSvcSection[];
  faqs: Array<{ q: string; a: string }>;
  steps: Array<{ tag: string; text: string }>;
  wordCount: number;
}

type Labels = {
  fields: string; audience: string; accepted: string; steps: string;
  faq: string; price: string; days: string; category: string; footer: string;
  priceUnit: string; daysUnit: string;
};

const LABELS: Record<PccLocale, Labels> = {
  en: { fields: "What ${name} includes", audience: "Who it serves", accepted: "Accepted by", steps: "How ordering works", faq: "Frequently asked questions", price: "Price", days: "Turnaround", category: "Tier", footer: "This page was assembled with a per-service seed — content is unique to this URL and not duplicated elsewhere on our site.", priceUnit: "", daysUnit: "business days" },
  zh: { fields: "${name} 包含项目", audience: "适用人群", accepted: "受理机构", steps: "下单流程", faq: "常见问题", price: "价格", days: "时效", category: "层级", footer: "本页面基于服务级独立种子生成,内容仅用于本 URL,不与我站其他页面重复。", priceUnit: "", daysUnit: "个工作日" },
  ja: { fields: "${name} 含有項目", audience: "対象者", accepted: "受理機関", steps: "注文手順", faq: "よくある質問", price: "料金", days: "所要日数", category: "ティア", footer: "本ページはサービス別シードで生成され、本 URL 専用の一意コンテンツです。", priceUnit: "", daysUnit: "営業日" },
  ko: { fields: "${name} 포함 항목", audience: "대상자", accepted: "접수 기관", steps: "주문 절차", faq: "자주 묻는 질문", price: "요금", days: "소요 기간", category: "티어", footer: "본 페이지는 서비스별 시드로 생성되어 이 URL 전용 고유 콘텐츠입니다.", priceUnit: "", daysUnit: "영업일" },
  vi: { fields: "${name} bao gồm", audience: "Đối tượng", accepted: "Cơ quan tiếp nhận", steps: "Quy trình đặt", faq: "Câu hỏi thường gặp", price: "Giá", days: "Thời gian", category: "Bậc", footer: "Trang này sinh từ seed theo dịch vụ — nội dung dành riêng cho URL này.", priceUnit: "", daysUnit: "ngày làm việc" },
  id: { fields: "${name} termasuk", audience: "Untuk siapa", accepted: "Diterima oleh", steps: "Cara memesan", faq: "Pertanyaan umum", price: "Harga", days: "Waktu proses", category: "Tier", footer: "Halaman ini dirakit dengan seed per layanan — konten unik untuk URL ini.", priceUnit: "", daysUnit: "hari kerja" },
};

export function buildPccLocaleServiceContent(locale: PccLocale, service: PccService): LocaleSvcContent {
  const safe = SAFE_PCC_COPY[locale];
  const f = (t: string) => t.replaceAll("{name}", service.nameEn);
  const h1 = f(safe.h1);
  const lead = f(safe.lead);
  const sections: LocaleSvcSection[] = safe.sections.map((x) => ({ heading: f(x.h), html: `<p>${f(x.body)}</p>` }));
  const faqs = safe.faqs.map((x) => ({ q: f(x.q), a: f(x.a) }));
  const steps = safe.steps.map((x) => ({ tag: x.tag, text: f(x.text) }));
  const totalText = [h1, lead, ...sections.flatMap((x) => [x.heading, x.html]), ...faqs.flatMap((x) => [x.q, x.a]), ...steps.map((x) => x.text)].join(" ");
  return { h1, lead, sections, faqs, steps, wordCount: totalText.split(/\s+/).length };
}

export function pccLocaleServiceLabels(locale: PccLocale) {
  const safe = SAFE_PCC_COPY[locale];
  return { ...LABELS[locale], accepted: safe.accepted, acceptedNote: safe.acceptedNote, daysValue: safe.daysValue, footer: "" };
}
